// Display formatting shared by every list, detail page and form.
//
// Money uses the same pinned en-US grouping as the dashboard's fmtMoney
// (components/dashboard/registry/format.ts): figures must tie out against the
// PDFs, which print en-US, whatever locale the viewer's browser reports.
const MONEY_LOCALE = 'en-US';

/**
 * `1234567.5` → `1,234,567.50`; with a currency, `৳ 1,234,567.50` / `EUR 1,234,567.50`.
 * Accepts the strings BigDecimal fields arrive as. Blank or non-numeric input renders `—`.
 */
export function formatMoney(value: number | string | null | undefined, currency?: string | null): string {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return '—';
    const text = n.toLocaleString(MONEY_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!currency) return text;
    return `${currency === 'BDT' ? '৳' : currency} ${text}`;
}

/**
 * A backend enum constant as a label: `PARTIALLY_PAID` → `Partially paid`,
 * `IN_WORK` → `In work`. Pass `overrides` where plain casing reads wrong
 * (`{ LAB_DIP: 'Lab dip', FOB: 'FOB' }`).
 */
/** Words that stay upper-case when an enum is humanised ("ERQ", not "Erq"; "FA segment transfer", not "Fa segment transfer"). */
const ENUM_ACRONYMS = new Set(['erq', 'fc', 'snd', 'edf', 'ltr', 'pad', 'od', 'bdt', 'usd', 'b2b', 'tds', 'ait',
    'fa', 'fx', 'vat', 'po', 'grn', 'cwip', 'lc', 'wht', 'gl', 'wppf', 'mfs', 'pdf', 'csv',
    'tt', 'swift', 'rtgs', 'beftn', 'npsb', 'eft', 'iban']);

/** An enum constant (or a bare word): letters, digits and underscores only. Anything else is free text. */
const ENUM_SHAPE = /^[A-Za-z0-9_]+$/;

export function humanizeEnum(value: string | null | undefined, overrides?: Record<string, string>): string {
    if (!value) return '—';
    if (overrides && overrides[value]) return overrides[value];
    // Free text a user typed ("TT (SWIFT)", "Cheque #1042") is shown as typed, never re-cased.
    if (!ENUM_SHAPE.test(value)) return value;
    const words = value.toLowerCase().split('_').filter(Boolean);
    if (words.length === 0) return value;
    const cased = words.map((w, i) => (ENUM_ACRONYMS.has(w) ? w.toUpperCase()
        : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w));
    return cased.join(' ');
}

/**
 * A unit price as entered: at least 2 decimals, but never rounds away extra precision
 * (`412.125` stays `412.125`, `1500` → `1,500.00`), so it agrees with the row math.
 */
export function formatUnitPrice(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(MONEY_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

/** Download name for one payslip, e.g. payslip-dic_rafiq_ahmed-2026-08 (no extension). */
export const payslipFileBase = (p: { userName?: string; year: number; month: number }): string =>
    `payslip-${(p.userName || 'employee').replace(/[^A-Za-z0-9_-]+/g, '_')}-${p.year}-${String(p.month).padStart(2, '0')}`;
